<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-end md:items-center justify-center"
  >
    <div
      class="absolute inset-0 bg-black bg-opacity-30"
      @click="$emit('close')"
    />
    <div class="relative bg-base-100 w-full md:w-96 md:max-h-[80vh] max-h-[70vh] rounded-t-xl md:rounded-xl shadow-xl flex flex-col">
      <div class="flex items-center justify-between px-4 py-3 border-b border-base-200">
        <h3 class="font-semibold text-lg">
          {{ t('comments') || 'Comments' }}
        </h3>
        <button
          class="btn btn-ghost btn-sm btn-circle"
          @click="$emit('close')"
        >
          <IconX :width="20" :height="20" />
        </button>
      </div>
      <div
        ref="commentsList"
        class="flex-1 overflow-y-auto px-4 py-3 space-y-4"
      >
        <div
          v-if="isLoading"
          class="flex justify-center py-8"
        >
          <IconInnerShadowTop class="animate-spin w-6 h-6" />
        </div>
        <div
          v-else-if="comments.length === 0"
          class="text-center py-8 text-gray-400"
        >
          {{ t('no_comments_yet') || 'No comments yet' }}
        </div>
        <template v-else>
          <div
            v-for="comment in comments"
            :key="comment.id"
            class="space-y-2"
          >
            <div class="bg-base-200 rounded-lg p-3">
              <div class="flex items-center justify-between mb-1">
                <span class="font-medium text-sm flex items-center gap-1">
                  {{ comment.author?.name || comment.submitter?.name || 'Unknown' }}
                  <span
                    v-if="comment.author_type === 'user'"
                    class="badge badge-xs badge-primary"
                  >{{ t('sender') || 'Sender' }}</span>
                </span>
                <span class="text-xs text-gray-400">{{ formatTime(comment.created_at) }}</span>
              </div>
              <p class="text-sm whitespace-pre-wrap">{{ comment.body }}</p>
              <div
                v-if="comment.resolved"
                class="mt-1 text-xs text-green-600 flex items-center space-x-1"
              >
                <IconCheck :width="14" :height="14" />
                <span>{{ t('resolved') || 'Resolved' }}</span>
              </div>
            </div>
            <div
              v-if="comment.replies && comment.replies.length"
              class="ml-4 space-y-2"
            >
              <div
                v-for="reply in comment.replies"
                :key="reply.id"
                class="bg-base-200 bg-opacity-50 rounded-lg p-3"
              >
                <div class="flex items-center justify-between mb-1">
                  <span class="font-medium text-sm flex items-center gap-1">
                    {{ reply.author?.name || reply.submitter?.name || 'Unknown' }}
                    <span
                      v-if="reply.author_type === 'user'"
                      class="badge badge-xs badge-primary"
                    >{{ t('sender') || 'Sender' }}</span>
                  </span>
                  <span class="text-xs text-gray-400">{{ formatTime(reply.created_at) }}</span>
                </div>
                <p class="text-sm whitespace-pre-wrap">{{ reply.body }}</p>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="px-4 py-3 border-t border-base-200">
        <form
          class="flex items-center space-x-2"
          @submit.prevent="submitComment"
        >
          <input
            v-model="newComment"
            type="text"
            class="input input-bordered input-sm flex-1"
            :placeholder="t('write_a_comment') || 'Write a comment...'"
            :disabled="isSending"
          >
          <button
            type="submit"
            class="btn btn-sm btn-neutral text-white"
            :disabled="!newComment.trim() || isSending"
          >
            <IconInnerShadowTop
              v-if="isSending"
              class="animate-spin w-4 h-4"
            />
            <IconSend
              v-else
              :width="16"
              :height="16"
            />
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { IconX, IconSend, IconInnerShadowTop, IconCheck } from '@tabler/icons-vue'

export default {
  name: 'CommentPanel',
  components: {
    IconX,
    IconSend,
    IconInnerShadowTop,
    IconCheck
  },
  inject: ['baseUrl', 't'],
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    submitterSlug: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  data () {
    return {
      comments: [],
      newComment: '',
      isLoading: false,
      isSending: false
    }
  },
  watch: {
    isOpen (value) {
      if (value) {
        this.loadComments()
      }
    }
  },
  methods: {
    loadComments () {
      this.isLoading = true

      fetch(this.baseUrl + `/s/${this.submitterSlug}/comments.json`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }).then((response) => {
        if (response.ok) {
          return response.json()
        }
        return []
      }).then((data) => {
        this.comments = data
      }).catch(() => {
        this.comments = []
      }).finally(() => {
        this.isLoading = false
        this.$nextTick(() => {
          if (this.$refs.commentsList) {
            this.$refs.commentsList.scrollTop = this.$refs.commentsList.scrollHeight
          }
        })
      })
    },
    submitComment () {
      if (!this.newComment.trim()) return

      this.isSending = true

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      fetch(this.baseUrl + `/s/${this.submitterSlug}/comments.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
        body: JSON.stringify({ body: this.newComment.trim() })
      }).then((response) => {
        if (response.ok) {
          return response.json()
        }
        throw new Error('Failed to post comment')
      }).then((comment) => {
        this.comments.push(comment)
        this.newComment = ''
        this.$nextTick(() => {
          if (this.$refs.commentsList) {
            this.$refs.commentsList.scrollTop = this.$refs.commentsList.scrollHeight
          }
        })
      }).catch((error) => {
        console.error(error)
      }).finally(() => {
        this.isSending = false
      })
    },
    formatTime (dateStr) {
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)

      if (minutes < 1) return this.t('just_now') || 'Just now'
      if (minutes < 60) return `${minutes}m`
      if (hours < 24) return `${hours}h`

      return date.toLocaleDateString()
    }
  }
}
</script>
